/** SF Symbol vocabulary from the App and the supplied 450-symbol inventory.
 * Lucide glyphs are approximations; no Apple assets or runtime icon loader. */
import type { SVGProps } from 'react';
import { clsx } from 'clsx';
import { Activity, AlarmClock, AlignLeft, Archive, ArrowDown, ArrowDownLeft, ArrowLeft, ArrowLeftRight, ArrowRight, ArrowUp, ArrowUpDown, ArrowUpRight, AtSign, AudioLines, Award, BadgeCheck, Ban, Bell, BellOff, Bike, Bold, BookOpen, Bookmark, Brain, Briefcase, BriefcaseBusiness, Building2, CalendarCheck, CalendarDays, CalendarX, Camera, Car, Cast, ChartColumn, ChartLine, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown, Circle, CircleAlert, CircleCheck, CircleDot, CircleMinus, CirclePlus, CircleQuestionMark, CircleStop, CircleUserRound, CircleX, ClipboardList, Clock, Cloud, CloudAlert, CloudOff, CloudSun, Code, Coffee, Command, Compass, Construction, Contact, ContactRound, Contrast, Copy, CornerDownLeft, CornerDownRight, CornerUpRight, Cpu, Crop, Download, Drama, Ellipsis, Eraser, ExternalLink, Eye, EyeOff, Fan, FastForward, FilePenLine, FilePlus, FileSearch, FileText, Film, Fingerprint, Flag, FlaskConical, Focus, Folder, FolderArchive, FolderCog, FolderMinus, FolderPlus, Forward, Gamepad2, Gauge, Gift, GitBranch, Globe, GraduationCap, Grid2X2, Hand, HardDrive, Hash, Heart, History, Hourglass, House, Image, Images, Inbox, Info, Italic, KeyRound, Keyboard, Languages, Laptop, Layers, LayoutGrid, Leaf, Lightbulb, Link, List, ListChecks, ListFilter, ListOrdered, ListTodo, LoaderCircle, Locate, LockKeyhole, LockKeyholeOpen, LogOut, Mail, Map, MapPin, Maximize2, MessageCircle, MessageCircleCheck, MessageCircleQuestionMark, MessageCircleWarning, MessageSquare, MessageSquarePlus, MessageSquareText, MessagesSquare, Mic, MicOff, Minimize2, Monitor, MonitorPlay, Moon, Mountain, MousePointer2, Music, Network, OctagonX, Paintbrush, Palette, PanelLeft, PanelRight, PanelsTopLeft, Paperclip, Pause, PenLine, Phone, PhoneOff, PictureInPicture2, Pin, Plane, Play, Plus, Pointer, QrCode, Quote, Radio, Ratio, RectangleHorizontal, Redo2, RefreshCw, RefreshCwOff, Repeat, RotateCcw, RotateCw, Rss, Scan, ScanLine, Scissors, Search, Send, Server, Settings, Settings2, Share, Shield, ShieldAlert, ShieldCheck, ShieldUser, ShoppingCart, Shuffle, SkipForward, SlidersHorizontal, Smartphone, SmartphoneNfc, Smile, Sparkles, SpellCheck, Square, SquareDashed, SquarePen, SquarePlus, Star, Strikethrough, Sun, SwitchCamera, Tablet, Tag, Target, Terminal, Ticket, Timer, TimerOff, Trash2, TriangleAlert, Type, Undo2, Unlink, UserRound, UserRoundCheck, UserRoundCog, UserRoundMinus, UserRoundPlus, UserRoundX, Users, UsersRound, Utensils, Video, VideoOff, Volume2, VolumeX, Wifi, WifiOff, Wrench, X, Zap, Columns3, Menu as MenuIcon, createLucideIcon, type LucideIcon } from 'lucide-react';

// Compound paths leave transparent counters, so filled SF variants work on any tile/background.
const solid = (name: string, d: string) => createLucideIcon(name, [['path', {
  d, fill: 'currentColor', fillRule: 'evenodd', strokeWidth: 0, key: 'solid',
}]]);
const FolderSolid = solid('HyloFolderSolid', 'M4 3h5l3 3h8a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z');
const paper = 'M6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z M14 3.5V8h4.5Z';
const FileSolid = solid('HyloFileSolid', paper);
const FileTextSolid = solid('HyloFileTextSolid', paper + ' M7 11h10v1.5H7Z M7 15h10v1.5H7Z M7 19h7v1H7Z');
const BookmarkSolid = solid('HyloBookmarkSolid', 'M6 2h12a1 1 0 0 1 1 1v19l-7-4-7 4V3a1 1 0 0 1 1-1Z');
const PlayRectangleSolid = solid('HyloPlayRectangleSolid', 'M4 3h16a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z M10 7v10l7-5Z');

export const iconMap = {
  'airplane': Plane,
  'airplayvideo': Cast,
  'alarm': AlarmClock,
  'alarm.fill': AlarmClock,
  'archivebox': Archive,
  'archivebox.fill': Archive,
  'arrow.clockwise': RefreshCw,
  'arrow.counterclockwise': RotateCcw,
  'arrow.down': ArrowDown,
  'arrow.down.app': Download,
  'arrow.down.circle': Download,
  'arrow.down.left': ArrowDownLeft,
  'arrow.down.right.and.arrow.up.left': Minimize2,
  'arrow.left': ArrowLeft,
  'arrow.left.arrow.right': ArrowLeftRight,
  'arrow.left.arrow.right.circle': ArrowLeftRight,
  'arrow.right': ArrowRight,
  'arrow.right.circle.fill': ArrowRight,
  'arrow.turn.up.right': CornerUpRight,
  'arrow.up.forward.square': ExternalLink,
  'arrow.triangle.2.circlepath': RefreshCw,
  'arrow.triangle.2.circlepath.camera': SwitchCamera,
  'arrow.triangle.branch': GitBranch,
  'arrow.triangle.swap': ArrowLeftRight,
  'arrow.trianglehead.2.clockwise': RefreshCw,
  'arrow.turn.down.right': CornerDownRight,
  'arrow.up': ArrowUp,
  'arrow.up.arrow.down': ArrowUpDown,
  'arrow.up.circle': ArrowUp,
  'arrow.up.circle.fill': ArrowUp,
  'arrow.up.forward': ArrowUpRight,
  'arrow.up.left.and.arrow.down.right': Maximize2,
  'arrow.up.right': ArrowUpRight,
  'arrow.up.right.square': ExternalLink,
  'arrow.uturn.backward': Undo2,
  'arrow.uturn.backward.circle.fill': Undo2,
  'arrow.uturn.forward': Redo2,
  'arrowshape.turn.up.left': Undo2,
  'arrowshape.turn.up.left.fill': Undo2,
  'arrowshape.turn.up.right': Forward,
  'aspectratio': Ratio,
  'at': AtSign,
  'bell': Bell,
  'bell.badge': Bell,
  'bell.badge.fill': Bell,
  'bell.fill': Bell,
  'bell.slash': BellOff,
  'bell.slash.fill': BellOff,
  'bicycle': Bike,
  'bold': Bold,
  'bolt': Zap,
  'bolt.fill': Zap,
  'bolt.trianglebadge.exclamationmark': TriangleAlert,
  'book': BookOpen,
  'book.closed.fill': BookOpen,
  'bookmark': Bookmark,
  'bookmark.fill': BookmarkSolid,
  'character.book.closed': BookOpen,
  'brain': Brain,
  'brain.head.profile': Brain,
  'brain.head.profile.fill': Brain,
  'briefcase': Briefcase,
  'bubble': MessageCircle,
  'bubble.left': MessageCircle,
  'bubble.left.and.bubble.right': MessagesSquare,
  'bubble.left.and.bubble.right.fill': MessagesSquare,
  'bubble.left.and.exclamationmark.bubble.right': MessageCircleWarning,
  'bubble.left.and.text.bubble.right': MessagesSquare,
  'bubble.left.and.text.bubble.right.fill': MessagesSquare,
  'bubble.left.fill': MessageCircle,
  'bubble.right': MessageCircle,
  'building.2': Building2,
  'building.2.fill': Building2,
  'calendar': CalendarDays,
  'calendar.badge.checkmark': CalendarCheck,
  'calendar.badge.clock': CalendarX,
  'camera': Camera,
  'camera.fill': Camera,
  'camera.metering.unknown': CircleQuestionMark,
  'car': Car,
  'cart': ShoppingCart,
  'character.book.closed.fill': BookOpen,
  'character.bubble': Languages,
  'chart.bar': ChartColumn,
  'chart.bar.fill': ChartColumn,
  'chart.xyaxis.line': ChartLine,
  'checklist': ListChecks,
  'checkmark': Check,
  'checkmark.bubble': MessageCircleCheck,
  'checkmark.bubble.fill': MessageCircleCheck,
  'checkmark.circle': CircleCheck,
  'checkmark.circle.fill': CircleCheck,
  'checkmark.message': MessageCircleCheck,
  'checkmark.message.fill': MessageCircleCheck,
  'checkmark.seal': BadgeCheck,
  'checkmark.seal.fill': BadgeCheck,
  'checkmark.shield': ShieldCheck,
  'checkmark.shield.fill': ShieldCheck,
  'chevron.backward': ChevronLeft,
  'chevron.compact.right': ChevronRight,
  'chevron.down': ChevronDown,
  'chevron.forward': ChevronRight,
  'chevron.left': ChevronLeft,
  'chevron.left.forwardslash.chevron.right': Code,
  'chevron.right': ChevronRight,
  'chevron.up': ChevronUp,
  'chevron.up.chevron.down': ChevronsUpDown,
  'circle': Circle,
  'circle.dotted': LoaderCircle,
  'circle.dotted.circle': LoaderCircle,
  'circle.fill': Circle,
  'circle.slash': Ban,
  'clock': Clock,
  'clock.arrow.circlepath': History,
  'clock.badge.xmark': TimerOff,
  'clock.fill': Clock,
  'cloud': Cloud,
  'command': Command,
  'cpu': Cpu,
  'cpu.fill': Cpu,
  'crop': Crop,
  'crown.fill': Award,
  'cup.and.saucer': Coffee,
  'curlybraces': Code,
  'curlybraces.square': Code,
  'cursorarrow': MousePointer2,
  'cylinder.split.1x2': Server,
  'desktopcomputer': Monitor,
  'dial.medium.fill': Gauge,
  'display': Monitor,
  'doc': FileText,
  'doc.badge.clock': BriefcaseBusiness,
  'doc.badge.ellipsis': FilePenLine,
  'doc.badge.gearshape': FilePenLine,
  'doc.badge.plus': FilePlus,
  'doc.exclamationmark': CircleAlert,
  'doc.fill': FileSolid,
  'doc.on.doc': Copy,
  'doc.on.doc.fill': Copy,
  'doc.questionmark': CircleQuestionMark,
  'doc.richtext': FilePenLine,
  'doc.richtext.fill': FileTextSolid,
  'doc.text': FileText,
  'doc.text.fill': FileTextSolid,
  'doc.text.magnifyingglass': FileSearch,
  'doc.zipper': FolderArchive,
  'document': FileText,
  'dot.radiowaves.left.and.right': Radio,
  'drop.halffull': Contrast,
  'ellipsis': Ellipsis,
  'ellipsis.circle': Ellipsis,
  'envelope': Mail,
  'exclamationmark': CircleAlert,
  'exclamationmark.arrow.trianglehead.2.clockwise.rotate.90': RefreshCwOff,
  'exclamationmark.bubble': MessageCircleWarning,
  'exclamationmark.bubble.fill': MessageCircleWarning,
  'exclamationmark.circle': CircleAlert,
  'exclamationmark.circle.fill': CircleAlert,
  'exclamationmark.icloud': CloudAlert,
  'exclamationmark.shield': ShieldAlert,
  'exclamationmark.shield.fill': ShieldAlert,
  'exclamationmark.triangle': TriangleAlert,
  'exclamationmark.triangle.fill': TriangleAlert,
  'externaldrive': HardDrive,
  'externaldrive.badge.exclamationmark': HardDrive,
  'externaldrive.badge.timemachine': History,
  'externaldrive.badge.xmark': HardDrive,
  'externaldrive.fill': HardDrive,
  'eye': Eye,
  'eye.fill': Eye,
  'eye.slash': EyeOff,
  'eye.slash.circle': EyeOff,
  'eye.slash.fill': EyeOff,
  'face.smiling': Smile,
  'faceid': Smile,
  'fan': Fan,
  'figure.hiking': Mountain,
  'film': Film,
  'finder': Monitor,
  'flag': Flag,
  'flag.2.crossed.fill': Flag,
  'flag.fill': Flag,
  'flag.slash': Flag,
  'flask': FlaskConical,
  'folder': Folder,
  'folder.badge.gearshape': FolderCog,
  'folder.badge.minus': FolderMinus,
  'folder.badge.plus': FolderPlus,
  'folder.badge.questionmark': Folder,
  'folder.fill': FolderSolid,
  'fork.knife': Utensils,
  'forward': FastForward,
  'forward.circle': FastForward,
  'forward.end.fill': SkipForward,
  'gamecontroller': Gamepad2,
  'gauge.medium': Gauge,
  'gauge.with.dots.needle.100percent': Gauge,
  'gauge.with.dots.needle.33percent': Gauge,
  'gauge.with.dots.needle.50percent': Gauge,
  'gearshape': Settings,
  'gearshape.2': Settings2,
  'gearshape.2.fill': Settings2,
  'gearshape.fill': Settings,
  'gift': Gift,
  'globe': Globe,
  'graduationcap': GraduationCap,
  'hammer.fill': Wrench,
  'hand.raised': Hand,
  'hand.raised.fill': Hand,
  'hand.raised.slash.fill': Hand,
  'hand.tap': Pointer,
  'hand.wave.fill': Hand,
  'heart': Heart,
  'heart.fill': Heart,
  'hourglass': Hourglass,
  'house': House,
  'house.fill': House,
  'icloud.slash': CloudOff,
  'info': Info,
  'info.circle': Info,
  'info.circle.fill': Info,
  'internaldrive.fill': HardDrive,
  'ipad': Tablet,
  'iphone': Smartphone,
  'iphone.slash': SmartphoneNfc,
  'italic': Italic,
  'key': KeyRound,
  'key.fill': KeyRound,
  'keyboard': Keyboard,
  'laptopcomputer': Laptop,
  'laptopcomputer.and.iphone': Laptop,
  'leaf': Leaf,
  'lightbulb': Lightbulb,
  'line.3.horizontal.decrease': ListFilter,
  'line.3.horizontal.decrease.circle': ListFilter,
  'line.3.horizontal.decrease.circle.fill': ListFilter,
  'line.diagonal': PenLine,
  'link': Link,
  'link.badge.minus': Unlink,
  'link.badge.plus': Link,
  'list.bullet': List,
  'list.bullet.clipboard': ClipboardList,
  'list.bullet.rectangle': ListTodo,
  'list.bullet.rectangle.portrait.fill': List,
  'list.number': ListOrdered,
  'location': MapPin,
  'location.fill': MapPin,
  'lock': LockKeyhole,
  'lock.fill': LockKeyhole,
  'lock.open': LockKeyholeOpen,
  'lock.open.fill': LockKeyholeOpen,
  'lock.rotation': Smartphone,
  'lock.shield': ShieldCheck,
  'lock.trianglebadge.exclamationmark': ShieldUser,
  'macmini': Monitor,
  'macpro.gen3': Monitor,
  'macstudio': Monitor,
  'macwindow': PanelsTopLeft,
  'magnifyingglass': Search,
  'map': Map,
  'mappin.and.ellipse': MapPin,
  'mic': Mic,
  'mic.circle': Mic,
  'mic.fill': Mic,
  'mic.slash.fill': MicOff,
  'minus.circle': CircleMinus,
  'minus.circle.fill': CircleMinus,
  'moon': Moon,
  'moon.stars.fill': Moon,
  'moon.zzz': Moon,
  'music.note': Music,
  'network': Network,
  'network.slash': WifiOff,
  'nosign': Ban,
  'note.text': FileText,
  'number': Hash,
  'opticid': Smile,
  'oval': Circle,
  'paintbrush': Paintbrush,
  'paintbrush.fill': Paintbrush,
  'paintbrush.pointed': Paintbrush,
  'paintpalette': Palette,
  'paintpalette.fill': Palette,
  'paperclip': Paperclip,
  'paperplane': Send,
  'paperplane.circle.fill': Send,
  'paperplane.fill': Send,
  'pause': Pause,
  'pause.circle': Pause,
  'pause.circle.fill': Pause,
  'pause.fill': Pause,
  'pencil': SquarePen,
  'pencil.circle': SquarePen,
  'pencil.circle.fill': SquarePen,
  'pencil.tip': SquarePen,
  'person': UserRound,
  'person.2': Users,
  'person.2.badge.gearshape': UserRoundCog,
  'person.2.fill': Users,
  'person.2.square.stack': Contact,
  'person.2.wave.2.fill': Users,
  'person.3': Users,
  'person.3.fill': Users,
  'person.3.sequence': UsersRound,
  'person.3.sequence.fill': UsersRound,
  'person.badge.clock': UserRoundCheck,
  'person.badge.key': UserRoundCog,
  'person.badge.minus': UserRoundMinus,
  'person.badge.plus': UserRoundPlus,
  'person.badge.shield.checkmark': UserRoundCheck,
  'person.crop.circle': CircleUserRound,
  'person.crop.circle.badge.checkmark': UserRoundCheck,
  'person.crop.circle.badge.exclamationmark': UserRoundCheck,
  'person.crop.circle.badge.minus': UserRoundMinus,
  'person.crop.circle.badge.plus': UserRoundPlus,
  'person.crop.circle.badge.questionmark': MessageCircleQuestionMark,
  'person.crop.circle.badge.xmark': UserRoundX,
  'person.crop.circle.fill': CircleUserRound,
  'person.crop.rectangle': ContactRound,
  'person.crop.rectangle.stack': Contact,
  'person.crop.rectangle.stack.fill': Contact,
  'person.crop.square': ContactRound,
  'person.fill': UserRound,
  'person.fill.xmark': UserRoundX,
  'person.text.rectangle': ContactRound,
  'person.text.rectangle.fill': ContactRound,
  'phone': Phone,
  'phone.down.fill': PhoneOff,
  'phone.fill': Phone,
  'photo': Image,
  'photo.badge.checkmark': Image,
  'photo.badge.exclamationmark': Image,
  'photo.fill': Image,
  'photo.fill.on.rectangle.fill': Images,
  'photo.on.rectangle': Images,
  'photo.on.rectangle.angled': Images,
  'pin': Pin,
  'pin.fill': Pin,
  'pin.slash': Pin,
  'play': Play,
  'play.circle': Play,
  'play.circle.fill': Play,
  'play.fill': Play,
  'play.rectangle': MonitorPlay,
  'play.rectangle.fill': PlayRectangleSolid,
  'plus': Plus,
  'plus.bubble': MessageSquarePlus,
  'plus.circle': CirclePlus,
  'plus.circle.fill': CirclePlus,
  'plus.square.on.square': SquarePlus,
  'point.3.connected.trianglepath.dotted': Network,
  'qrcode': QrCode,
  'qrcode.viewfinder': ScanLine,
  'questionmark': CircleQuestionMark,
  'questionmark.bubble': MessageCircleQuestionMark,
  'questionmark.circle': CircleQuestionMark,
  'questionmark.circle.fill': CircleQuestionMark,
  'questionmark.square.dashed': SquareDashed,
  'record.circle': CircleDot,
  'record.circle.fill': CircleDot,
  'rectangle': RectangleHorizontal,
  'rectangle.3.group': PanelsTopLeft,
  'rectangle.3.group.fill': PanelsTopLeft,
  'rectangle.fill': RectangleHorizontal,
  'rectangle.inset.filled': PictureInPicture2,
  'rectangle.on.rectangle.slash': PanelsTopLeft,
  'rectangle.portrait.and.arrow.right': LogOut,
  'rectangle.portrait.and.arrow.right.fill': LogOut,
  'rectangle.stack.fill': Layers,
  'rectangle.stack.person.crop': Contact,
  'repeat': Repeat,
  'rotate.left': RotateCcw,
  'rotate.right': RotateCw,
  'safari': Compass,
  'safari.fill': Compass,
  'scissors': Scissors,
  'scope': Locate,
  'shield': Shield,
  'shield.lefthalf.filled': Shield,
  'shuffle': Shuffle,
  'sidebar.left': PanelLeft,
  'sidebar.right': PanelRight,
  'slider.horizontal.3': SlidersHorizontal,
  'smartphone': Smartphone,
  'sparkles': Sparkles,
  'speaker.slash.fill': VolumeX,
  'speaker.wave.2': Volume2,
  'speaker.wave.2.fill': Volume2,
  'square': Square,
  'square.and.arrow.down': Download,
  'square.and.arrow.up': Share,
  'square.and.arrow.up.fill': Share,
  'square.and.pencil': SquarePen,
  'square.dashed': SquareDashed,
  'square.grid.2x2': Grid2X2,
  'square.grid.2x2.fill': Grid2X2,
  'square.grid.3x3.fill': LayoutGrid,
  'square.on.square.intersection.dashed': Scan,
  'square.stack.3d.up': Layers,
  'star': Star,
  'star.fill': Star,
  'star.slash': Star,
  'stop': Square,
  'stop.circle': CircleStop,
  'stop.fill': Square,
  'strikethrough': Strikethrough,
  'sun.horizon.fill': CloudSun,
  'sun.max': Sun,
  'tag': Tag,
  'tag.fill': Tag,
  'tag.slash': Tag,
  'target': Target,
  'terminal': Terminal,
  'terminal.fill': Terminal,
  'text.alignleft': AlignLeft,
  'text.badge.checkmark': SpellCheck,
  'text.book.closed.fill': BookOpen,
  'text.bubble': MessageSquareText,
  'text.bubble.fill': MessageSquare,
  'text.magnifyingglass': Search,
  'text.quote': Quote,
  'textformat': Type,
  'textformat.size': Type,
  'theatermasks': Drama,
  'theatermasks.fill': Drama,
  'ticket': Ticket,
  'timer': Timer,
  'touchid': Fingerprint,
  'trash': Trash2,
  'trash.fill': Trash2,
  'tray': Inbox,
  'tray.and.arrow.down.fill': Archive,
  'tray.and.arrow.up': Send,
  'tray.full': Inbox,
  'tray.full.fill': Inbox,
  'video': Video,
  'video.fill': Video,
  'video.slash.fill': VideoOff,
  'viewfinder': Focus,
  'wand.and.sparkles': Sparkles,
  'wand.and.stars': Sparkles,
  'wave.3.right': Rss,
  'waveform': AudioLines,
  'waveform.path': AudioLines,
  'waveform.path.ecg': Activity,
  'waveform.path.ecg.rectangle': Activity,
  'wifi': Wifi,
  'wifi.exclamationmark': Wifi,
  'wifi.slash': WifiOff,
  'wrench.and.screwdriver': Construction,
  'wrench.and.screwdriver.fill': Construction,
  'xmark': X,
  'xmark.circle': CircleX,
  'xmark.circle.fill': CircleX,
  'xmark.octagon': OctagonX,
  'xmark.octagon.fill': OctagonX,
  'eraser': Eraser,
  'line.3.horizontal': MenuIcon,
  'rectangle.on.rectangle': Copy,
  'rectangle.split.3x1': Columns3,
  'return': CornerDownLeft,
} satisfies Record<string, LucideIcon>;

export type SymbolName = keyof typeof iconMap;
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> { name: SymbolName | (string & {}); size?: number | string; label?: string; strokeWidth?: number }
export function Icon({ name, size, label, strokeWidth = 1.8, className, ...props }: IconProps) {
  const Glyph = Object.hasOwn(iconMap, name) ? (iconMap as Record<string, LucideIcon>)[name]! : CircleQuestionMark;
  return <Glyph {...props} width={size ?? '1em'} height={size ?? '1em'} strokeWidth={strokeWidth}
    style={{ inlineSize: size, blockSize: size, ...props.style }}
    role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}
    focusable="false" className={clsx('hylo-icon', className)} />;
}
